import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidLabeledCard } from '../../components/rid-labeled-card/rid-labeled-card';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidWordMatrix } from '../../components/rid-word-matrix/rid-word-matrix';
import { TRidWord } from '../../components/rid-word/rid-word';

@Component({
  selector: 'rid-reading-matrix-categorise',
  imports: [
    RidMechanicBackground,
    RidTopBar,
    RidLabeledCard,
    RidWordMatrix,
    RidOverlayFeedback,
    RidMechanicTitle,
    RidMechanicWrapper,
    RidMechanicContent,
    RidBottomButton,
  ],
  templateUrl: './rid-reading-matrix-categorise.html',
  host: {
    class: 'block',
  },
})
export class RidReadingMatrixCategorise {
  matrix = input.required<{ word: string; isCorrect: boolean }[][]>();
  category = input.required<string>();

  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  state = signal<{ type: 'SELECTING' } | { type: 'FEEDBACK' }>({ type: 'SELECTING' });
  selection = signal<[col: number, row: number][]>([]);

  matrixWithAppearance = computed(() => {
    if (this.state().type === 'FEEDBACK') {
      return this.matrix().map((col, cIndex) =>
        col.map((word, rIndex) => {
          return {
            word: word.word,
            appearance: (isSelected) =>
              this.matrix()[cIndex][rIndex].isCorrect ? (isSelected ? 'CORRECT' : 'INCORRECT') : isSelected ? 'INCORRECT' : 'DEFAULT',
            isSelectable: false,
          } satisfies TRidWord;
        }),
      );
    } else {
      return this.matrix().map((col) =>
        col.map((word) => ({ word: word.word, appearance: 'DEFAULT', isSelectable: true }) satisfies TRidWord),
      );
    }
  });

  finished = output<void>();

  onSelectionChanged(event: { kind: 'ADDED' | 'REMOVED'; index: [col: number, row: number] }) {
    if (event.kind === 'ADDED') {
      this.selection.set([...this.selection(), event.index]);
    } else {
      this.selection.set(this.selection().filter((sel) => sel[0] !== event.index[0] || sel[1] !== event.index[1]));
    }
  }

  async evaluate() {
    const isCorrect =
      this.selection().every(([col, row]) => this.matrix()[col][row].isCorrect) &&
      this.selection().length ===
        this.matrix()
          .flat()
          .filter((item) => item.isCorrect).length;

    this.state.set({ type: 'FEEDBACK' });

    if (!isCorrect) {
      await this.overlayFeedback().showIncorrect();
    } else {
      await this.overlayFeedback().showCorrect();
    }
  }

  finish() {
    this.finished.emit();
  }
}
